import React, { useContext, useEffect, useState } from "react";
import styles from "./Settings.module.scss";
import Input from "shared/components/Input/Input";
import Button from "shared/components/Buttons/Button";
import apiCalls from "shared/utils/apiCalls";
import AppContext from "context/AppContext";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { ReducerTypes } from "shared/types/reducerTypes";
import { CheckCircleRounded } from "@material-ui/icons";
import Skeleton from "@material-ui/lab/Skeleton";
import TextareaAutosize from "@material-ui/core/TextareaAutosize";
import cn from "classnames";
import { Grid } from "@material-ui/core";
import readmeExmample from "./readmeExample";

const themeOptions = [
  {
    value: "default",
    label: "Default",
    image:
      "https://firebasestorage.googleapis.com/v0/b/devchallenges-1234.appspot.com/o/themes%2Fdefault.png?alt=media&token=381ad176-cd08-4c25-9092-3499d43ad24c",
  },
  {
    value: "instagram",
    label: "Instagram",
    image:
      "https://firebasestorage.googleapis.com/v0/b/devchallenges-1234.appspot.com/o/themes%2Finstagram.png?alt=media&token=463048c8-e3ef-4525-8500-7316179e9b13",
  },
];

const Settings = () => {
  const {
    state: { user, loadingUser },
    dispatch,
  } = useContext(AppContext);

  const [loading, setLoading] = useState<boolean>();
  const [showSaved, setShowSaved] = useState<boolean>(false);
  const [readme, setReadme] = useState<string>("");
  const [readmeError, setReadmeError] = useState<string | undefined>();
  const [themeValue, setThemeValue] = useState<string>("default");

  const validationSchema = yup.object({
    title: yup.string().min(0).max(70),
    linkedin: yup.string().url().min(0).max(70),
    twitter: yup.string().url().min(0).max(70),
    facebook: yup.string().url().min(0).max(70),
    instagram: yup.string().url().min(0).max(70),
    youtube: yup.string().url().min(0).max(70),
    twitch: yup.string().url().min(0).max(70),
  });

  const { register, handleSubmit, errors, setValue } = useForm({
    validationSchema,
  });

  const onSubmit = async (data: any) => {
    setReadmeError(undefined);
    setLoading(true);
    setShowSaved(false);

    if (readme.length > 5000) {
      setReadmeError("README cannot be more than 5000 charactor");
      setLoading(false);
      return;
    }

    const res = await apiCalls(
      {
        method: "post",
        data: { ...data, portfolioTheme: themeValue, readme },
        url: "/user/additionalInfo",
      },
      dispatch
    );

    if (res) {
      dispatch({
        type: ReducerTypes.getUser,
        payload: { value: res.data },
      });

      setShowSaved(true);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (user && user.title) {
      setValue("title", user.title);
    }

    if (user && user.linkedin) {
      setValue("linkedin", user.linkedin);
    }

    if (user && user.twitter) {
      setValue("twitter", user.twitter);
    }

    if (user && user.facebook) {
      setValue("facebook", user.facebook);
    }

    if (user && user.instagram) {
      setValue("instagram", user.instagram);
    }

    if (user && user.youtube) {
      setValue("youtube", user.youtube);
    }

    if (user && user.twitch) {
      setValue("twitch", user.twitch);
    }

    if (user && user.readme) {
      setReadme(user.readme);
    }

    if (user && user.portfolioTheme) {
      setThemeValue(user.portfolioTheme);
    }

    // eslint-disable-next-line
  }, [user]);

  if (loadingUser) {
    return (
      <div>
        <Skeleton height={400} variant="rect" />

        <div style={{ marginBottom: "var(--space-lg)" }} />

        <Skeleton height={300} variant="rect" />
      </div>
    );
  }

  if (!user) {
    return <div>Not found</div>;
  }

  return (
    <div className={styles.settings}>
      <h1>Settings</h1>

      <section className={styles.settings_section}>
        <div className={styles.settings_section_heading}>
          <h5 style={{ marginBottom: "var(--space-2xs)", fontWeight: 500 }}>
            Profile
          </h5>
          <div className={styles.settings_section_heading_description}>
            To edit your profile, edit at{" "}
            <a
              href="https://github.com/settings/profile"
              target="_blank"
              rel="noopener noreferrer"
            >
              Github
            </a>{" "}
            and re-login again.
          </div>
        </div>

        <div className={styles.settings_section_row}>
          <div className={styles.settings_section_row_label}>Display name</div>
          <div className={styles.settings_section_row_value}>{user.name}</div>
        </div>

        <div className={styles.settings_section_row}>
          <div className={styles.settings_section_row_label}>Bio</div>
          <div className={styles.settings_section_row_value}>{user.bio}</div>
        </div>

        <div className={styles.settings_section_row}>
          <div className={styles.settings_section_row_label}>Email</div>
          <div className={styles.settings_section_row_value}>{user.email}</div>
        </div>

        <div className={styles.settings_section_row}>
          <div className={styles.settings_section_row_label}>Website</div>
          <div className={styles.settings_section_row_value}>{user.blog}</div>
        </div>

        <div className={styles.settings_section_row}>
          <div className={styles.settings_section_row_label}>Github</div>
          <div className={styles.settings_section_row_value}>
            {user.githubUrl}
          </div>
        </div>

        {/* <div className={styles.settings_section_row}>
          <div className={styles.settings_section_row_label}>Twitter</div>
          <div className={styles.settings_section_row_value}>
            @{user.twitter_username}
          </div>
        </div> */}
      </section>

      <section className={styles.settings_section}>
        <div className={styles.settings_section_heading}>
          <h5 style={{ marginBottom: "var(--space-2xs)", fontWeight: 500 }}>
            Additional information
          </h5>
          <div className={styles.settings_section_heading_description}>
            Your information may be shown in your portfolio or profile.
          </div>
        </div>

        <form
          className={styles.settings_section_form}
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className={styles.settings_section_form_content}>
            <Input
              style={{ color: "red", borderColor: "red" }}
              InputLabelProps={{ shrink: true }}
              name="title"
              label="Employment Title"
              variant="outlined"
              fullWidth
              margin="normal"
              inputRef={register}
              error={!!errors["title"]?.message}
              helperText={errors["title"] ? errors["title"].message : undefined}
              placeholder="Employment Title"
            />

            <Input
              style={{ color: "red", borderColor: "red" }}
              InputLabelProps={{ shrink: true }}
              name="linkedin"
              label="Linkedin"
              variant="outlined"
              fullWidth
              margin="normal"
              inputRef={register}
              error={!!errors["linkedin"]?.message}
              helperText={
                errors["linkedin"] ? errors["linkedin"].message : undefined
              }
              placeholder="https://..."
            />

            <Input
              style={{ color: "red", borderColor: "red" }}
              InputLabelProps={{ shrink: true }}
              name="twitter"
              label="Twitter URL"
              variant="outlined"
              fullWidth
              margin="normal"
              inputRef={register}
              error={!!errors["twitter"]?.message}
              helperText={
                errors["twitter"] ? errors["twitter"].message : undefined
              }
              placeholder="https://..."
            />

            <Input
              style={{ color: "red", borderColor: "red" }}
              InputLabelProps={{ shrink: true }}
              name="facebook"
              label="Facebook URL"
              variant="outlined"
              fullWidth
              margin="normal"
              inputRef={register}
              error={!!errors["facebook"]?.message}
              helperText={
                errors["facebook"] ? errors["facebook"].message : undefined
              }
              placeholder="https://..."
            />

            <Input
              style={{ color: "red", borderColor: "red" }}
              InputLabelProps={{ shrink: true }}
              name="instagram"
              label="Instagram URL"
              variant="outlined"
              fullWidth
              margin="normal"
              inputRef={register}
              error={!!errors["instagram"]?.message}
              helperText={
                errors["instagram"] ? errors["instagram"].message : undefined
              }
              placeholder="https://..."
            />

            <Input
              style={{ color: "red", borderColor: "red" }}
              InputLabelProps={{ shrink: true }}
              name="youtube"
              label="Youtube URL"
              variant="outlined"
              fullWidth
              margin="normal"
              inputRef={register}
              error={!!errors["youtube"]?.message}
              helperText={
                errors["youtube"] ? errors["youtube"].message : undefined
              }
              placeholder="https://..."
            />

            <Input
              style={{ color: "red", borderColor: "red" }}
              InputLabelProps={{ shrink: true }}
              name="twitch"
              label="Twitch URL"
              variant="outlined"
              fullWidth
              margin="normal"
              inputRef={register}
              error={!!errors["twitch"]?.message}
              helperText={
                errors["twitch"] ? errors["twitch"].message : undefined
              }
              placeholder="https://..."
            />

            <TextareaAutosize
              onChange={(e: any) => setReadme(e.target.value)}
              value={readme}
              className={styles.settings_section_form_textInput}
              aria-label="readme"
              rowsMin={5}
              placeholder="About me"
            />

            {readmeExmample.map(({ content, title }) => (
              <button
                type="button"
                className={styles.settings_section_form_exampleReadme}
                onClick={() => setReadme(content)}
              >
                {title}
              </button>
            ))}

            <div className={styles.settings_section_form_helperText}>
              {readmeError ? (
                <div style={{ color: "var(--red)" }}>{readmeError}</div>
              ) : (
                <div>
                  More templates can be found from{" "}
                  <a
                    href="https://github.com/kautukkundan/Awesome-Profile-README-templates"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    here.
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className={styles.settings_section_form_themeOptions}>
            <h5>Portfolio Theme</h5>

            <Grid container spacing={3}>
              {themeOptions.map(({ label, image, value }) => (
                <Grid item xs={12} md={3}>
                  <button
                    type="button"
                    className={cn(styles.settings_section_form_themeOption, {
                      [styles.settings_section_form_themeOption__active]:
                        value === themeValue,
                    })}
                    onClick={() => setThemeValue(value)}
                  >
                    <label>{label}</label>
                    <img src={image} alt="label"></img>
                  </button>
                </Grid>
              ))}
            </Grid>
          </div>

          <div className={styles.settings_section_form_buttonRow}>
            <Button
              type="submit"
              aria-label="submit"
              buttonVariant="secondary"
              loading={loadingUser || loading}
            >
              Save
            </Button>

            {showSaved && (
              <div className={styles.settings_section_form_notification}>
                <CheckCircleRounded color="inherit" fontSize="small" />{" "}
                <div
                  className={styles.settings_section_form_notification_message}
                >
                  Saved
                </div>
              </div>
            )}
          </div>
        </form>
      </section>
    </div>
  );
};

export default Settings;
